/** biome-ignore-all lint/style/useFilenamingConvention: <explanation> */
import {
  FAIL,
  FETCH_COLORS,
  FETCH_EXIT_URL,
  FETCH_HAS_FACEBOOK_CONVERSION_PIXEL,
  FETCH_INPUT_TYPES,
  FETCH_PAGE,
  FETCH_PAGE_FILES,
  FETCH_USER,
  PAGE_VALIDATION_ERROR,
  PAGE_VALIDATION_ERROR_RESET,
  PREVIEW_PAGE,
  RESET_PAGE_ERROR,
  SAVE_PAGE,
  SUCCESS,
  TOGGLE_DOUBLE_OPT_IN,
  UPDATE_CONTENT_UPDATED_AT,
} from 'client/actionTypes'
import * as pageApi from 'client/api/pageApi'
import { validateFormInputs, validatePage } from 'client/utils/validators'
import { PageValidationError } from 'common/errors/PageValidationError'
import { getFilteredEntitiesIdsByPageType } from 'common/utils/entityUtils'
import { batch } from 'react-redux'
import { convertUserDataToPageUserData } from 'src/editor/website/utils/convert-user-data-to-page-user-data'
import fixIsMasterBlockRootCommandForClient from 'tools/commands/fixIsMasterBlockRootCommand/fixIsMasterBlockRootCommandForClient'

export const fetchPageDataFailure = error => ({
  type: FETCH_PAGE + FAIL,
  payload: error,
})

const fetchPageSuccess = content => ({
  type: FETCH_PAGE + SUCCESS,
  payload: content,
})

export const fetchColorsSuccess = content => ({
  type: FETCH_COLORS + SUCCESS,
  payload: content,
})

const fetchHasFacebookConversionPixelSuccess = content => ({
  type: FETCH_HAS_FACEBOOK_CONVERSION_PIXEL + SUCCESS,
  payload: content,
})

const fetchUserSuccess = (user, rollbar) => {
  if (!user) {
    return {}
  }

  rollbar.configure({
    payload: {
      person: {
        id: user.id,
        username: user.name,
        email: user.email,
      },
    },
  })

  return {
    type: FETCH_USER + SUCCESS,
    payload: user,
  }
}

const fetchInputTypesSuccess = inputTypes => ({
  type: FETCH_INPUT_TYPES + SUCCESS,
  payload: inputTypes,
})

const fetchExitUrl = inputTypes => ({
  type: FETCH_EXIT_URL,
  payload: inputTypes,
})

const fetchFilesSuccess = files => ({
  type: FETCH_PAGE_FILES,
  payload: files,
})

export const savePageSuccess = () => ({
  type: SAVE_PAGE,
})

export const pageValidationFail = errors => ({
  type: PAGE_VALIDATION_ERROR,
  payload: errors,
})

export const previewPageFail = error => ({
  type: PREVIEW_PAGE + FAIL,
  payload: error,
})

export const savePageFail = error => ({
  type: SAVE_PAGE + FAIL,
  payload: error,
})

export const updateContentUpdatedAt = contentUpdatedAt => ({
  type: UPDATE_CONTENT_UPDATED_AT,
  payload: contentUpdatedAt,
})

export const fetchPageDataSuccess = (data, rollbar) => async dispatch => {
  batch(() => {
    dispatch(fetchPageSuccess(data.page))
    dispatch(fetchFilesSuccess(data.files))
    if (data.fields) {
      dispatch(fetchInputTypesSuccess(data.fields))
    }
    if (data.user) {
      dispatch(fetchUserSuccess(data.user, rollbar))
    }
    if (data.colors) {
      dispatch(fetchColorsSuccess(data.colors))
    }
    if (data.has_facebook_conversion_pixel) {
      dispatch(
        fetchHasFacebookConversionPixelSuccess(
          data.has_facebook_conversion_pixel,
        ),
      )
    }
    dispatch(fetchExitUrl(data.exitUrl))
  })
}

export const fetchWebsitePageDataSuccess =
  (data, user, rollbar) => async dispatch => {
    try {
      data.user = convertUserDataToPageUserData(user)
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: <explanation>
      console.error('Error converting user data to page user data', error)
    }
    dispatch(fetchPageDataSuccess(data, rollbar))
  }

export const previewPage = isTemplate => (dispatch, getState) => {
  const { present } = getState().page

  const errors = validatePage(present)

  if (errors.length > 0) {
    throw new PageValidationError(errors)
  }

  return pageApi.previewPage(present, isTemplate)
}

export const getPreviewPageData = () => (dispatch, getState) => {
  const { present } = getState().page
  const { entities, id, seo, tracking, type, locale } = present

  return { content: { entities, seo, tracking }, id, type, locale }
}

export const savePage = (isTemplate, rollbar) => async (dispatch, getState) => {
  const {
    page: { present },
    payment,
    inputTypes,
  } = getState()

  const errors = validatePage(present, payment)

  if (errors.length > 0) {
    throw new PageValidationError(errors)
  }

  if (payment.paymentMethods.length > 0) {
    validateFormInputs(present, payment.paymentMethods, inputTypes)
  }
  const page = { ...getState().page.present }

  const contentUpdatedAt = page.contentUpdatedAt

  page.entities = Object.values(page.entities).reduce((acc, entity) => {
    if (entity.isReadOnly) {
      return acc
    }

    return {
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...acc,
      [entity.id]: entity,
    }
  }, {})

  const excludedEntitiesIds = getFilteredEntitiesIdsByPageType(
    page.entities,
    page.type,
  )

  if (excludedEntitiesIds.length > 0) {
    page.entities = Object.entries(page.entities).reduce(
      (acc, [entityId, entity]) => {
        if (excludedEntitiesIds.includes(entityId)) {
          // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
          return { ...acc }
        }
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        return { ...acc, [entityId]: entity }
      },
      {},
    )
  }
  page.entities = fixIsMasterBlockRootCommandForClient(page.entities)[0]

  const response = await pageApi.savePage(page, isTemplate)
  const previousContentUpdatedAt = response.data?.previousContentUpdatedAt
  const newContentUpdatedAt = response.data?.newContentUpdatedAt
  if (
    previousContentUpdatedAt &&
    previousContentUpdatedAt !== contentUpdatedAt
  ) {
    /* NOTE: temporary disable the rollbar warning to avoid exceeding the rollbar item quota
     rollbar.warning(
      `The last saved version of the page (contentUpdatedAt="${previousContentUpdatedAt}") was overwritten 
      by a page modified from an older version (contentUpdatedAt="${contentUpdatedAt}").
      `,
    ) */
  }
  dispatch(updateContentUpdatedAt(newContentUpdatedAt))
}

export const resetPageError = () => dispatch => {
  dispatch({
    type: RESET_PAGE_ERROR,
  })
}

export const resetPageValidationError = () => dispatch => {
  dispatch({
    type: PAGE_VALIDATION_ERROR_RESET,
  })
}

export const toggleDoubleOptIn = () => dispatch => {
  dispatch({
    type: TOGGLE_DOUBLE_OPT_IN,
  })
}
